import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit, Print } from '@mui/icons-material'

import {
  useDELETE_behaviorMutation,
  useGET_behaviorByIdQuery,
  usePATCH_behaviorMutation,
  usePOST_behaviorMutation,
} from 'redux/api/behaviorApi'
import { useGET_groupCourseSessionSelectQuery } from 'redux/api/sessionsApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/behaviorModel'
import { crudColorMap, crudIconMap, printDiv } from 'utils/helpers'

import { ActionType, IBehaviorData } from 'types'
import SliderField from 'components/atoms/fields/SliderField'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  report?: boolean
}

const BehaviorForm: FC<IProps> = ({ action, id, viewOnly, report = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = report ? '/behaviour-reports' : '/behaviour'
  const editSlug = `/${report ? 'behaviour-reports' : 'behaviour'}/edit/${id}` 

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.behaviour')}`,
    entityId: id,
    getEntityQuery: useGET_behaviorByIdQuery,
    createEntityMutation: usePOST_behaviorMutation,
    editEntityMutation: usePATCH_behaviorMutation,
    deleteEntityMutation: useDELETE_behaviorMutation,
  })

  const { student, incident_level, incident_date, comment } = formModel()

  return (
    <Formik
      enableReinitialize
      initialValues={entity || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IBehaviorData = {
              student_id: values.student.id,
              incident_level: Number(values.incident_level),
              incident_date: values.incident_date,
              comment: values.comment,
            }

        handleSubmit(action, id, data)
      }}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
            <Grid item xs={12} md={4}>
              <SelectField
                fieldProps={{ ...student }}
                apiCall={useGET_studentsProfileSelectQuery}
                disabled={viewOnly}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DatePickerField fieldProps={{ ...incident_date }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={4}>
              <SliderField
                  fieldProps={{ ...incident_level }}
                  disabled={viewOnly}
                  min={0}
                  max={100}
                  step={5}
                />
            </Grid>

            <Grid item xs={12} md={12}>
              <RichTextField
                fieldProps={{ ...comment }}
                disabled={viewOnly}
                label={`${t('common.comment')}`}
              />
            </Grid>


            <Grid item xs={12}>
              <Box className="transparent-background" sx={{ display: 'flex' }}>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>

                {action === 'view' && report && 
                  <Button
                  variant='outlined'
                  color='black'
                  onClick={ () => { printDiv('template') } }
                  sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                >
                  <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.print')}`}
                </Button>
               }

                {action === 'view' && !report &&
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
                }

                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          <div id="template" style={{ display: 'none' }}>
            <table className="border-table"><tbody>  
              <tr><th style={{ width: '40%' }}><b>{ student.label }:</b></th><td>{ entity?.student?.title }</td></tr>
              <tr><th><b>{ incident_level.label }:</b></th><td>{ entity?.incident_level }</td></tr>
              <tr><th><b>{ incident_date.label }:</b></th><td>{ entity?.incident_date }</td></tr>
              <tr><th><b>{ comment.label }:</b></th><td>{ entity?.comment }</td></tr>
            </tbody></table>    
          </div>

        </Form>
      )}
    </Formik>
  )
}
export default BehaviorForm
